import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import FilterPanel from './FilterPanel';
import DataTable from './DataTable';
import AddressPagination from './AddressPagination';
import PODsModal from './PODsModal';
import CoordinatesComparisonMapModal from './CoordinatesComparisonMapModal';
import AddressManagementService, { Address, AddressQueryParams, getAllDeliveredCoordinates } from '../../services/addressManagementService';

const DEFAULT_PAGE_SIZE = 50;

const AddressManagement: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [addresses, setAddresses] = useState<Address[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    const [selectedAddressForPODs, setSelectedAddressForPODs] = useState<Address | null>(null);
    const [mapPreviewAddress, setMapPreviewAddress] = useState<Address | null>(null);
    const [mapPreviewLoadingTraceId, setMapPreviewLoadingTraceId] = useState<string | null>(null);

    // Parse initial filters from URL or use defaults
    const getInitialFilters = (): AddressQueryParams => {
        const filters: AddressQueryParams = {
            offset: 0,
            limit: DEFAULT_PAGE_SIZE,
            orderBy: 'createdAt',
            orderByDescending: true
        };

        if (searchParams.get('isGoogleValid')) filters.isGoogleValid = searchParams.get('isGoogleValid') === 'true';
        if (searchParams.get('isRadarValid')) filters.isRadarValid = searchParams.get('isRadarValid') === 'true';
        if (searchParams.get('geoFenceConfidence')) filters.geoFenceConfidence = searchParams.get('geoFenceConfidence') || undefined;
        if (searchParams.get('googlePossibleNextAction')) filters.googlePossibleNextAction = searchParams.get('googlePossibleNextAction') || undefined;
        if (searchParams.get('radarGeoconfidence')) filters.radarGeoconfidence = searchParams.get('radarGeoconfidence') || undefined;
        if (searchParams.get('radarVerificationStatus')) filters.radarVerificationStatus = searchParams.get('radarVerificationStatus') || undefined;
        if (searchParams.get('isApartment')) filters.isApartment = searchParams.get('isApartment') === 'true';
        if (searchParams.get('destinationType')) filters.destinationType = searchParams.get('destinationType') || undefined;
        if (searchParams.get('hasPod')) filters.hasPod = searchParams.get('hasPod') === 'true';
        if (searchParams.get('dateFrom')) filters.dateFrom = searchParams.get('dateFrom') || undefined;
        if (searchParams.get('dateTo')) filters.dateTo = searchParams.get('dateTo') || undefined;
        if (searchParams.get('traceIds')) filters.traceIds = searchParams.get('traceIds') || undefined;
        if (searchParams.get('fullAddress')) filters.fullAddress = searchParams.get('fullAddress') || undefined;
        if (searchParams.get('coordinateSource')) filters.coordinateSource = searchParams.get('coordinateSource') || undefined;
        if (searchParams.get('hasGeminiPreprocessing')) filters.hasGeminiPreprocessing = searchParams.get('hasGeminiPreprocessing') === 'true';
        if (searchParams.get('geminiStatus')) filters.geminiStatus = searchParams.get('geminiStatus') || undefined;
        if (searchParams.get('geminiCorrectionAccepted')) filters.geminiCorrectionAccepted = searchParams.get('geminiCorrectionAccepted') === 'true';
        if (searchParams.get('offset')) filters.offset = parseInt(searchParams.get('offset')!, 10);
        // limit is handled but typically fixed to 50 in UI for now
        if (searchParams.get('orderBy')) filters.orderBy = searchParams.get('orderBy')!;
        if (searchParams.get('orderByDescending')) filters.orderByDescending = searchParams.get('orderByDescending') === 'true';

        return filters;
    };

    const [filters, setFilters] = useState<AddressQueryParams>(getInitialFilters);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Sync state to URL
    useEffect(() => {
        const params: Record<string, string> = {};
        if (filters.isGoogleValid) params.isGoogleValid = 'true';
        if (filters.isRadarValid) params.isRadarValid = 'true';
        if (filters.geoFenceConfidence) params.geoFenceConfidence = filters.geoFenceConfidence;
        if (filters.googlePossibleNextAction) params.googlePossibleNextAction = filters.googlePossibleNextAction;
        if (filters.radarGeoconfidence) params.radarGeoconfidence = filters.radarGeoconfidence;
        if (filters.radarVerificationStatus) params.radarVerificationStatus = filters.radarVerificationStatus;
        if (filters.isApartment) params.isApartment = 'true';
        if (filters.destinationType) params.destinationType = filters.destinationType;
        if (filters.hasPod !== undefined) params.hasPod = filters.hasPod ? 'true' : 'false';
        if (filters.dateFrom) params.dateFrom = filters.dateFrom;
        if (filters.dateTo) params.dateTo = filters.dateTo;
        if (filters.traceIds) params.traceIds = filters.traceIds;
        if (filters.fullAddress) params.fullAddress = filters.fullAddress;
        if (filters.coordinateSource) params.coordinateSource = filters.coordinateSource;
        if (filters.hasGeminiPreprocessing !== undefined) params.hasGeminiPreprocessing = filters.hasGeminiPreprocessing ? 'true' : 'false';
        if (filters.geminiStatus) params.geminiStatus = filters.geminiStatus;
        if (filters.geminiCorrectionAccepted !== undefined) params.geminiCorrectionAccepted = filters.geminiCorrectionAccepted ? 'true' : 'false';

        if (filters.offset !== undefined && filters.offset > 0) params.offset = filters.offset.toString();
        // params.limit = DEFAULT_PAGE_SIZE.toString(); // Optional to show in URL
        if (filters.orderBy && filters.orderBy !== 'createdAt') params.orderBy = filters.orderBy;
        if (filters.orderByDescending !== undefined && filters.orderByDescending !== true) params.orderByDescending = 'false';

        setSearchParams(params, { replace: true });
    }, [filters, setSearchParams]);

    const fetchAddresses = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await AddressManagementService.getAddresses(filters);
            setAddresses(response.items);
            setTotalItems(response.totalItems);
        } catch (err: any) {
            setError(err?.response?.data?.message || err.message || "An error occurred while fetching addresses.");
            setAddresses([]);
            setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchAddresses();
    }, [fetchAddresses]);

    const handleFilterChange = (newFilters: AddressQueryParams) => {
        setFilters(newFilters);
    };

    const handleClearFilters = () => {
        setFilters({
            offset: 0,
            limit: DEFAULT_PAGE_SIZE,
            orderBy: 'createdAt',
            orderByDescending: true
        });
    };

    const handleSorting = (field: string) => {
        setFilters(prev => {
            const isSameField = prev.orderBy === field;
            const newDescending = isSameField ? !prev.orderByDescending : true; // Default to desc for new field
            return {
                ...prev,
                orderBy: field,
                orderByDescending: newDescending,
                offset: 0 // Reset to first page on sort
            };
        });
    };

    const handlePageChange = (page: number) => {
        const newOffset = (page - 1) * DEFAULT_PAGE_SIZE;
        setFilters(prev => ({
            ...prev,
            offset: newOffset
        }));
    };

    const handleViewPODs = (address: Address) => {
        setSelectedAddressForPODs(address);
    };

    const handleViewDetails = (address: Address) => {
        if (address.traceId) {
            navigate(`/address-management/${address.traceId}`);
        }
    };

    const handleClosePODsModal = () => {
        setSelectedAddressForPODs(null);
    };

    const handlePreviewMap = async (address: Address) => {
        if (!address.traceId) return;

        setMapPreviewLoadingTraceId(address.traceId);
        try {
            const fullAddress = await AddressManagementService.getAddressProfile(address.traceId);
            setMapPreviewAddress(fullAddress);
        } catch (err) {
            console.error('Failed to load coordinates for map preview:', err);
        } finally {
            setMapPreviewLoadingTraceId(null);
        }
    };

    const handleCloseMapPreview = () => {
        setMapPreviewAddress(null);
    };

    const currentPage = Math.floor((filters.offset || 0) / DEFAULT_PAGE_SIZE) + 1;
    const totalPages = Math.ceil(totalItems / DEFAULT_PAGE_SIZE);

    return (
        <div className="container-fluid py-4 pb-5 mb-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1">Address Management</h2>
                    <p className="text-muted mb-4">Manage and monitor address validations</p>
                </div>
                <div className="d-flex gap-2 align-items-start">
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setIsFilterOpen(true)} disabled={isLoading}>
                        <i className="bi bi-funnel-fill me-1"></i>
                        Filter
                    </button>
                    <button className="btn btn-outline-primary btn-sm" onClick={fetchAddresses} disabled={isLoading}>
                        <i className={`${isLoading ? 'spinner-border spinner-border-sm' : 'bi bi-arrow-clockwise'} me-1`}></i>
                        Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                </div>
            )}

            <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                isLoading={isLoading}
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
            />

            <DataTable
                data={addresses}
                isLoading={isLoading}
                onSort={handleSorting}
                orderBy={filters.orderBy}
                orderByDescending={filters.orderByDescending}
                onViewPODs={handleViewPODs}
                onViewDetails={handleViewDetails}
                onPreviewMap={handlePreviewMap}
                mapPreviewLoadingTraceId={mapPreviewLoadingTraceId}
            />

            <AddressPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={DEFAULT_PAGE_SIZE}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                isLoading={isLoading}
            />

            {selectedAddressForPODs && (
                <PODsModal
                    traceId={selectedAddressForPODs.traceId || ''}
                    isOpen={!!selectedAddressForPODs}
                    onClose={handleClosePODsModal}
                />
            )}

            {mapPreviewAddress && (
                <CoordinatesComparisonMapModal
                    show={!!mapPreviewAddress}
                    onClose={handleCloseMapPreview}
                    traceId={mapPreviewAddress.traceId || undefined}
                    fullAddress={`${mapPreviewAddress.street1}${mapPreviewAddress.street2 ? ', ' + mapPreviewAddress.street2 : ''}, ${mapPreviewAddress.city}, ${mapPreviewAddress.stateCode} ${mapPreviewAddress.zip}`}
                    googleCoordinates={
                        mapPreviewAddress.googleLatitude && mapPreviewAddress.googleLongitude
                            ? { lat: mapPreviewAddress.googleLatitude, lng: mapPreviewAddress.googleLongitude }
                            : null
                    }
                    radarCoordinates={
                        mapPreviewAddress.radarLatitude && mapPreviewAddress.radarLongitude
                            ? { lat: mapPreviewAddress.radarLatitude, lng: mapPreviewAddress.radarLongitude }
                            : null
                    }
                    uberCoordinates={
                        mapPreviewAddress.uberLatitude && mapPreviewAddress.uberLongitude
                            ? { lat: mapPreviewAddress.uberLatitude, lng: mapPreviewAddress.uberLongitude }
                            : null
                    }
                    podMarkers={getAllDeliveredCoordinates(mapPreviewAddress)}
                />
            )}
        </div>
    );
};

export default AddressManagement;
